import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Save,
  Eye,
  History,
  Smartphone,
  Monitor,
  Loader2,
  RotateCcw,
  Mail,
  Palette,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import {
  EmailTemplate,
  EmailTemplateDesign,
  EmailTemplateVersion,
  useEmailTemplate,
  useEmailTemplateVersions,
  useUpdateEmailTemplate,
  useRevertToVersion,
  SAMPLE_PREVIEW_DATA,
  sanitizeHtml,
  renderTemplate,
  buildFullEmailHtml,
} from "@/hooks/useEmailTemplates";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

interface EmailTemplateEditorProps {
  templateId: string;
  onBack: () => void;
}

export function EmailTemplateEditor({ templateId, onBack }: EmailTemplateEditorProps) {
  const { data: template, isLoading } = useEmailTemplate(templateId);
  const { data: versions = [] } = useEmailTemplateVersions(templateId);
  const updateTemplate = useUpdateEmailTemplate();
  const revertToVersion = useRevertToVersion();

  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [design, setDesign] = useState<EmailTemplateDesign>({
    primaryColor: "#1C61E0",    // EAB Dark Blue
    textColor: "#1a1a1a",
    backgroundColor: "#f5f5f5",
    buttonColor: "#F39001",     // EAB Orange
    buttonTextColor: "#ffffff",
    footerText: "© {{year}} Entrepreneurs Across Borders. All rights reserved.",
  });
  const [isActive, setIsActive] = useState(true);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Load template data
  useEffect(() => {
    if (template) {
      setSubject(template.subject_template);
      setHtmlContent(template.html_template);
      setDesign(template.design_json);
      setIsActive(template.is_active);
      setHasUnsavedChanges(false);
    }
  }, [template]);

  // Track changes
  useEffect(() => {
    if (template) {
      const changed =
        subject !== template.subject_template ||
        htmlContent !== template.html_template ||
        JSON.stringify(design) !== JSON.stringify(template.design_json) ||
        isActive !== template.is_active;
      setHasUnsavedChanges(changed);
    }
  }, [subject, htmlContent, design, isActive, template]);

  const handleSave = async () => {
    const sanitizedHtml = sanitizeHtml(htmlContent);
    
    await updateTemplate.mutateAsync({
      id: templateId,
      updates: {
        subject_template: subject,
        html_template: sanitizedHtml,
        design_json: design,
        is_active: isActive,
      },
    });
    
    setHtmlContent(sanitizedHtml);
    setHasUnsavedChanges(false);
  };

  const handleRevert = async (version: EmailTemplateVersion) => {
    await revertToVersion.mutateAsync({
      templateId,
      version,
    });
    setShowVersionHistory(false);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onBack();
    }
  };

  // Send test email mutation
  const sendTestEmail = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error("No email found for current user");

      const { data, error } = await supabase.functions.invoke("send-test-email", {
        body: {
          templateId,
          recipientEmail: user.email,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Test email sent to your email address");
    },
    onError: (error) => {
      toast.error("Failed to send test email: " + error.message);
    },
  });

  // Render preview
  const renderPreview = () => {
    const renderedBody = renderTemplate(htmlContent, SAMPLE_PREVIEW_DATA, design);
    return buildFullEmailHtml(renderedBody, design);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Template not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {template.name}
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                  Unsaved
                </Badge>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              Key: <code className="bg-muted px-1 rounded">{template.key}</code>
              {template.description && ` • ${template.description}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-4">
            <Switch
              id="is-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="is-active" className="text-sm">
              Active
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVersionHistory(true)}
          >
            <History className="h-4 w-4 mr-1" />
            History
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sendTestEmail.mutate()}
            disabled={sendTestEmail.isPending}
          >
            {sendTestEmail.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-1" />
            )}
            Send Test
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateTemplate.isPending || !hasUnsavedChanges}
          >
            {updateTemplate.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor Panel */}
        <div className="space-y-4">
          <Tabs defaultValue="content">
            <TabsList>
              <TabsTrigger value="content" className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                Content
              </TabsTrigger>
              <TabsTrigger value="design" className="flex items-center gap-1">
                <Palette className="h-4 w-4" />
                Design
              </TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject Line</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject..."
                />
                <p className="text-xs text-muted-foreground">
                  Available variables: {"{{first_name}}"}, {"{{full_name}}"}, {"{{app_name}}"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="html-content">Email Body (HTML)</Label>
                <Textarea
                  id="html-content"
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="<h1>Hello {{first_name}}</h1>..."
                  className="font-mono text-sm min-h-[400px]"
                />
                <div className="bg-muted/50 p-3 rounded-lg text-xs space-y-2">
                  <p className="font-medium">Button Syntax (URLs are locked):</p>
                  <code className="block bg-background px-2 py-1 rounded">
                    {"{{BUTTON_PRIMARY label=\"Click Here\" action=\"dashboard\"}}"}
                  </code>
                  <p className="text-muted-foreground">
                    Available actions: dashboard, directory, connections, profile, view_connection, 
                    view_message, view_help_request, view_discussion, schedule_call
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="design" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Colors</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="primary-color" className="text-xs">Primary/Header Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="primary-color"
                          type="color"
                          value={design.primaryColor}
                          onChange={(e) => setDesign({ ...design, primaryColor: e.target.value })}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.primaryColor}
                          onChange={(e) => setDesign({ ...design, primaryColor: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="text-color" className="text-xs">Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="text-color"
                          type="color"
                          value={design.textColor}
                          onChange={(e) => setDesign({ ...design, textColor: e.target.value })}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.textColor}
                          onChange={(e) => setDesign({ ...design, textColor: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bg-color" className="text-xs">Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="bg-color"
                          type="color"
                          value={design.backgroundColor}
                          onChange={(e) => setDesign({ ...design, backgroundColor: e.target.value })}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.backgroundColor}
                          onChange={(e) => setDesign({ ...design, backgroundColor: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="button-color" className="text-xs">Button Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="button-color"
                          type="color"
                          value={design.buttonColor}
                          onChange={(e) => setDesign({ ...design, buttonColor: e.target.value })}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.buttonColor}
                          onChange={(e) => setDesign({ ...design, buttonColor: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="button-text-color" className="text-xs">Button Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          id="button-text-color"
                          type="color"
                          value={design.buttonTextColor}
                          onChange={(e) => setDesign({ ...design, buttonTextColor: e.target.value })}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.buttonTextColor}
                          onChange={(e) => setDesign({ ...design, buttonTextColor: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Header & Footer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="logo-url" className="text-xs">Header Logo URL (optional)</Label>
                    <Input
                      id="logo-url"
                      value={design.headerLogoUrl || ""}
                      onChange={(e) => setDesign({ ...design, headerLogoUrl: e.target.value || undefined })}
                      placeholder="https://..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to show "EAB Connect" text header
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="footer-text" className="text-xs">Footer Text</Label>
                    <Input
                      id="footer-text"
                      value={design.footerText}
                      onChange={(e) => setDesign({ ...design, footerText: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use {"{{year}}"} for current year
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={previewMode === "desktop" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setPreviewMode("desktop")}
                  >
                    <Monitor className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewMode === "mobile" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setPreviewMode("mobile")}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-2 rounded-lg mb-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Subject:</strong> {renderTemplate(subject, SAMPLE_PREVIEW_DATA, design)}
                </p>
              </div>
              <div
                className={`border rounded-lg overflow-hidden bg-white ${
                  previewMode === "mobile" ? "max-w-[375px] mx-auto" : ""
                }`}
              >
                <iframe
                  srcDoc={renderPreview()}
                  className="w-full h-[500px] border-0"
                  title="Email Preview"
                  sandbox="allow-same-origin"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Preview uses sample data – no real user information
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </DialogTitle>
            <DialogDescription>
              View and restore previous versions of this template
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {versions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No previous versions yet
              </p>
            ) : (
              <div className="space-y-2">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">Version {version.version_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(version.created_at), "PPP 'at' p")}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevert(version)}
                      disabled={revertToVersion.isPending}
                    >
                      {revertToVersion.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restore
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning */}
      <Dialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnsavedWarning(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowUnsavedWarning(false);
                onBack();
              }}
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
